import React, { useCallback, useMemo, useRef } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import { BottomSheetTab } from "./index";
import BroadcastChatTabs from "../broadcast-chat/chat-tabs";
import BroadcastControl from "../broadcast-control";
import BroadcastViewersTab from "../broadcast-tabs-viewers";
import MobileBroadcastSupportForm from "./mobile-broadcast-support-form";
import { ChatState } from "../_stores/chat/enums";
import { IShowType, MainTab } from "../_stores/broadcast/enums";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindIcon from "library/components/_tailwind/icon";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";

interface MobileBroadcastBottomSheetProps {
  broadcastStore?: BroadcastStore;
  nodeChatStore?: NodeChatStore;
  activeTab: BottomSheetTab;
  isExpanded: boolean;
  isLandscape?: boolean;
  onTabChange: (tab: BottomSheetTab) => void;
  onToggleExpanded: () => void;
  onExpand: () => void;
  onCollapse: () => void;
}

const MobileBroadcastBottomSheet: React.FC<MobileBroadcastBottomSheetProps> = ({
  broadcastStore,
  nodeChatStore,
  activeTab,
  isExpanded,
  isLandscape = false,
  onTabChange,
  onToggleExpanded,
  onExpand,
  onCollapse,
}) => {
  const { isShowStarted, currentShowType, setCurrentMainTab } = broadcastStore!;
  const { memberCount, numberOfOnlineGuests, chatState } = nodeChatStore!;

  const isChatReady = chatState === ChatState.started;

  // Swipe gesture handling
  const touchStartY = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 30; // Minimum distance in pixels to register as swipe

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null) return;

      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchEndY - touchStartY.current;

      // Swipe down (positive delta) = collapse
      if (deltaY > SWIPE_THRESHOLD) {
        onCollapse();
      }
      // Swipe up (negative delta) = expand
      else if (deltaY < -SWIPE_THRESHOLD) {
        onExpand();
      }

      touchStartY.current = null;
    },
    [onExpand, onCollapse]
  );

  const numberOfMembers = useMemo(() => {
    return isShowStarted ? memberCount : 0;
  }, [memberCount, isShowStarted]);

  const numberOfGuests = useMemo(() => {
    if (!isShowStarted) return 0;
    if (
      currentShowType === IShowType.FREE ||
      currentShowType === IShowType.TIPPING
    ) {
      return numberOfOnlineGuests;
    }
    return 0;
  }, [isShowStarted, numberOfOnlineGuests, currentShowType]);

  const handleTabClick = useCallback(
    (tab: BottomSheetTab) => {
      if (tab === "showModes") {
        setCurrentMainTab(MainTab.SHOW_TYPES);
      } else if (tab === "viewers") {
        setCurrentMainTab(MainTab.VIEWERS);
      }
      onTabChange(tab);
    },
    [onTabChange, setCurrentMainTab]
  );

  const handleWhisperClick = useCallback(() => {
    onTabChange("chat");
  }, [onTabChange]);

  const tabStyle = {
    flex: 1,
    background: "transparent",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const activeTabStyle = {
    ...tabStyle,
    background: "rgba(0, 0, 0, 0.08)",
  };

  return (
    <TailwindFlex
      position='absolute'
      inset={["bottom-0", "left-0", "right-0"]}
      flexDirection='flex-col'
      borderRadius={["rounded-t-2xl"]}
      zIndex='z-20'
      backgroundColor='bg-primary-bg-color'
      style={{
        height: isExpanded && !isLandscape ? "65%" : "auto",
        maxHeight: isLandscape ? "100%" : "65%",
        transition: "all 0.3s ease",
        paddingBottom: "env(safe-area-inset-bottom)",
        boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
      }}>
      {/* Drag handle */}
      <div
        className='flex justify-center py-3 cursor-pointer'
        onClick={onToggleExpanded}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}>
        <TailwindBox
          width='w-10'
          height='h-1'
          borderRadius='rounded-full'
          backgroundColor='bg-gray-300'
        />
      </div>

      {/* Tab buttons - only show when expanded */}
      {isExpanded && (
        <TailwindFlex
          gap='gap-1'
          padding={["px-3", "pb-2"]}
          borderWidth={["border-b"]}
          borderColor='border-gray-200'>
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            gap='gap-1.5'
            padding={["py-2.5", "px-3"]}
            textColor='text-main-color'
            opacity={activeTab === "chat" ? "opacity-100" : "opacity-50"}
            fontSize='text-sm'
            fontWeight='font-medium'
            onClick={() => handleTabClick("chat")}
            style={activeTab === "chat" ? activeTabStyle : tabStyle}>
            <TailwindIcon name='chat' fontSize='text-base' />
            <TailwindTranslatedText
              fontSize='text-sm'
              fontWeight='font-medium'
              descriptor={{
                id: "broadcast.chatTab",
                defaultMessage: "Chat",
              }}
            />
          </TailwindBox>

          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            gap='gap-1.5'
            padding={["py-2.5", "px-3"]}
            textColor='text-gray-800'
            opacity={activeTab === "showModes" ? "opacity-100" : "opacity-50"}
            fontSize='text-sm'
            fontWeight='font-medium'
            onClick={() => handleTabClick("showModes")}
            style={activeTab === "showModes" ? activeTabStyle : tabStyle}>
            <TailwindIcon name='theaters' fontSize='text-base' />
            <TailwindTranslatedText
              fontSize='text-sm'
              fontWeight='font-medium'
              descriptor={{
                id: "common.show-modes",
                defaultMessage: "Show Types",
              }}
            />
          </TailwindBox>

          {isShowStarted && (
            <TailwindBox
              as='button'
              display='flex'
              alignItems='items-center'
              justifyContent='justify-center'
              gap='gap-1.5'
              padding={["py-2.5", "px-3"]}
              textColor='text-gray-800'
              opacity={activeTab === "viewers" ? "opacity-100" : "opacity-50"}
              fontSize='text-sm'
              fontWeight='font-medium'
              onClick={() => handleTabClick("viewers")}
              style={activeTab === "viewers" ? activeTabStyle : tabStyle}>
              <TailwindIcon name='people' fontSize='text-base' />
              <TailwindTranslatedText
                fontSize='text-sm'
                fontWeight='font-medium'
                descriptor={{
                  id: "broadcast.viewer-tab",
                  defaultMessage:
                    "{numberOfMembers} Member(s) / {numberOfGuests} Guest(s)",
                }}
                values={{
                  numberOfMembers,
                  numberOfGuests,
                }}
              />
            </TailwindBox>
          )}

          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            gap='gap-1.5'
            padding={["py-2.5", "px-3"]}
            textColor='text-gray-800'
            opacity={activeTab === "support" ? "opacity-100" : "opacity-50"}
            fontSize='text-sm'
            fontWeight='font-medium'
            onClick={() => handleTabClick("support")}
            style={activeTab === "support" ? activeTabStyle : tabStyle}>
            <TailwindIcon name='support' fontSize='text-base' />
            <TailwindTranslatedText
              fontSize='text-sm'
              fontWeight='font-medium'
              descriptor={{
                id: "broadcast.supportTab",
                defaultMessage: "Support",
              }}
            />
          </TailwindBox>
        </TailwindFlex>
      )}

      {/* Tab content - only show when expanded */}
      {isExpanded && (
        <TailwindFlex
          flexGrow='flex-grow'
          overflow='overflow-hidden'
          flexDirection='flex-col'>
          {/* Chat tab */}
          {activeTab === "chat" && (
            <TailwindFlex
              flexGrow='flex-grow'
              overflow='overflow-hidden'
              flexDirection='flex-col'>
              {isChatReady && isShowStarted ? (
                <BroadcastChatTabs />
              ) : (
                <TailwindFlex
                  flexDirection='flex-col'
                  alignItems='items-center'
                  justifyContent='justify-center'
                  height='h-full'
                  gap='gap-3'
                  padding={["px-4"]}
                  textColor='text-gray-400'>
                  <TailwindIcon
                    name='chat_bubble_outline'
                    fontSize='text-3xl'
                  />
                  {isShowStarted ? (
                    <TailwindTranslatedText
                      fontSize='text-sm'
                      descriptor={{
                        id: "broadcast.chatLoading",
                        defaultMessage: "Chat Is Loading",
                      }}
                    />
                  ) : (
                    <TailwindTranslatedText
                      fontSize='text-sm'
                      textAlign='text-center'
                      descriptor={{
                        id: "broadcast.chatInstruct1",
                        defaultMessage: "Welcome to the Streamray Broadcaster.",
                      }}
                    />
                  )}
                </TailwindFlex>
              )}
            </TailwindFlex>
          )}

          {/* Show Modes tab */}
          {activeTab === "showModes" && (
            <TailwindFlex flexGrow='flex-grow' overflow={["overflow-y-auto"]}>
              <BroadcastControl />
            </TailwindFlex>
          )}

          {/* Viewers tab */}
          {activeTab === "viewers" && isShowStarted && (
            <TailwindFlex
              flexGrow='flex-grow'
              overflow={["overflow-y-auto"]}
              padding={["p-3"]}>
              <BroadcastViewersTab onWhisperClick={handleWhisperClick} />
            </TailwindFlex>
          )}

          {/* Support tab */}
          {activeTab === "support" && (
            <TailwindFlex
              flexGrow='flex-grow'
              overflow={["overflow-y-auto"]}>
              <MobileBroadcastSupportForm />
            </TailwindFlex>
          )}
        </TailwindFlex>
      )}
    </TailwindFlex>
  );
};

export default inject(
  "broadcastStore",
  "nodeChatStore"
)(observer(MobileBroadcastBottomSheet));
